import firebase from "firebase/app"

import { Collections } from "Collections"
import { getinviteExpirationTimestamp } from "Constants"
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils"
import { AnalyticsEvent } from "models/AnalyticsEvent"
import { AppUser } from "models/AppUser"
import { emailFrom } from "models/Email"
import { EnqueueSnacbar } from "models/EnqueueSnacbar"
import { InviteResolution } from "models/InviteResolution"
import { Org } from "models/Org"
import { OrgRoles } from "models/OrgRoles"
import { OrgUser } from "models/OrgUser"
import { OrgUserEmailInvite } from "models/OrgUserEmailInvite"
import { removeId } from "Util/removeId"

export function createEmailInvite(org: Org, inviterOrgUser: OrgUser, inviterAppUser: AppUser, emailToInvite: string, roles: OrgRoles.OrgRole[], enqueueSnacbar: EnqueueSnacbar) {
    FirebaseUtils.createDocument<OrgUserEmailInvite>(
        true,
        Collections.ORG_USER_EMAIL_INVITES,
        null,
        inviterAppUser,
        inviterOrgUser,
        AnalyticsEvent.CREATED_ORG_EMAIL_INVITE,
        AnalyticsEvent.CREATED_ORG_EMAIL_INVITE,
        AnalyticsEvent.CREATE_ORG_EMAIL_INVITE_ERROR,
        enqueueSnacbar,
        'Invitasjon sendt til E-post: ' + emailToInvite,
        {
            invitedUser: {
                appUserEmail: emailFrom(emailToInvite)
            },
            invite: {
                expiresAt: getinviteExpirationTimestamp(),
                org: org,
                allottedRoles: {
                    map: roles.reduce((acc, role) => ({ ...acc, [role]: true }), {}),
                },
                inviterUser: {
                    appUser: inviterAppUser,
                    orgUser: inviterOrgUser,
                },
                resolution: null,
                active: true,
            },
        },
        (doc) => ({
            inviteId: doc.id,
        })
    )
}

export function withdrawEmailInvite(orgUser: OrgUser, appUser: AppUser, invite: OrgUserEmailInvite, enqueueSnacbar: EnqueueSnacbar) {
    FirebaseUtils.updateDocument<OrgUserEmailInvite>(
        true,
        Collections.ORG_USER_EMAIL_INVITES,
        invite.id,
        appUser,
        orgUser,
        AnalyticsEvent.WITHDREW_ORG_EMAIL_INVITE,
        AnalyticsEvent.WITHDREW_ORG_EMAIL_INVITE,
        AnalyticsEvent.WITHDRAW_ORG_EMAIL_INVITE_ERROR,
        enqueueSnacbar,
        'Tilbakedro invitasjon',
        {
            ...invite,
            invite: {
                ...invite.invite,
                active: false,
                resolution: {
                    status: InviteResolution.WITHDRAWN,
                    resolvedBy: {
                        appUser,
                        orgUser,
                    },
                    resolvedAt: firebase.firestore.Timestamp.now(),
                },
            }
        },
        (doc) => ({
            inviteId: doc.id,
        })
    )
}

function addressInvite(appUser: AppUser, invite: OrgUserEmailInvite, resolutionStatus: InviteResolution.ACCEPTED | InviteResolution.REJECTED, enqueueSnacbar: EnqueueSnacbar, updateEvent: AnalyticsEvent) {
    FirebaseUtils.updateDocument<OrgUserEmailInvite>(
        true,
        Collections.ORG_USER_EMAIL_INVITES,
        invite.id,
        appUser,
        null,
        updateEvent,
        AnalyticsEvent.ADDRESSED_ORG_EMAIL_INVITE,
        AnalyticsEvent.WITHDRAW_ORG_EMAIL_INVITE_ERROR,
        enqueueSnacbar,
        resolutionStatus === InviteResolution.ACCEPTED ? 'Aksepterte invitasjonen' : 'Avviste invitasjonen',
        {
            ...removeId(invite),
            invite: {
                ...invite.invite,
                active: false,
                resolution: {
                    status: resolutionStatus,
                    resolvedBy: {
                        // Rejections on email updates should be anonymous
                        appUser: resolutionStatus === InviteResolution.REJECTED ? null : appUser,
                        orgUser: null,
                    },
                    resolvedAt: firebase.firestore.Timestamp.now(),
                },
            },
        },
        (doc) => ({
            inviteId: doc.id,
        }),
        {
            // Rejections on email updates should be anonymous
            anonymousUpdate: resolutionStatus === InviteResolution.REJECTED,
        }
    )
}

export function acceptEmailInvite(appUser: AppUser, invite: OrgUserEmailInvite, enqueueSnacbar: EnqueueSnacbar) {
    addressInvite(appUser, invite, InviteResolution.ACCEPTED, enqueueSnacbar, AnalyticsEvent.ACCEPTED_ORG_EMAIL_INVITE)
}

export function rejectEmailInvite(appUser: AppUser, invite: OrgUserEmailInvite, enqueueSnacbar: EnqueueSnacbar) {
    addressInvite(appUser, invite, InviteResolution.REJECTED, enqueueSnacbar, AnalyticsEvent.REJECTED_ORG_EMAIL_INVITE)
}
